<script setup lang="ts">
/**
 * 枚举控件。
 *
 * 注意：「枚举」是**控件形态**，不是值类型 ——
 * 它只约束「直接编辑时能选什么」，不约束连线（端口类型以 socket.type 为准）。
 * 所以同一个控件既能编辑字符串枚举（left/center/right），
 * 也能编辑数字枚举（DPI 203/300、字重 400/700）。
 */
import type { EnumWidget } from '../../types/widget'

const props = defineProps<{ widget: EnumWidget }>()
const emit = defineEmits<{ (e: 'update', value: string | number): void }>()

function onChange(ev: Event) {
  const raw = (ev.target as HTMLSelectElement).value
  // <select> 的值永远是字符串，所以按字符串比对找回原始值，
  // 这样数字枚举能原样回传成 number，不会悄悄变成 '203'。
  const matched = props.widget.options.find((o) => String(o.value) === raw)
  emit('update', matched ? matched.value : raw)
}
</script>

<template>
  <div class="np-widget np-widget--enum nodrag" :title="props.widget.hint">
    <!--
      ⚠️ `:key` 跟着值走 + option 上标 `:selected`：`<select>` 的选中项按规范是
      "最后一个带 selected 的 option，没有就归第一个"。只在 select 上绑 `:value`
      是不够的 —— Vue 更新时先设 value（那时还没有 option）、后插 option，
      浏览器就把选中项归到**第一个**，用户选完立刻弹回去
      （真机反馈："面板属性里也改不了『拉伸』，只能使用『完整放入』"；
      面板那边是同一个坑，见 `panels.ts` 的 `enumField`）。

      ⚠️ 无头环境（happy-dom）里 `select.value` 不一定会跟着 `selected` 走，
      所以验证脚本读的是 option 上的 `selected` 属性，而不是 `select.value`。
    -->
    <select
      :key="`${props.widget.id}:${String(props.widget.value)}`"
      class="np-widget__field"
      :value="String(props.widget.value)"
      :disabled="props.widget.disabled"
      @change="onChange"
      @pointerdown.stop
    >
      <option
        v-for="opt in props.widget.options"
        :key="String(opt.value)"
        :value="String(opt.value)"
        :selected="String(opt.value) === String(props.widget.value)"
      >
        {{ opt.label }}
      </option>
    </select>
    <span class="np-widget__caret" aria-hidden="true">
      <svg viewBox="0 0 10 10" fill="none">
        <path
          d="M1.5 3.5 L5 7 L8.5 3.5"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  </div>
</template>
